import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { User, Calendar, Award } from "lucide-react";

const CertificatesDisplay = ({ userId }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000,
      );

      const url = userId
        ? `/certifications?id_user=${userId}`
        : `/certifications`;
      const response = await apiClient.get(url, { cancelToken: source.token });

      clearTimeout(timeoutId);

      const data = response.data;
      if (Array.isArray(data)) {
        setCertificates(data);
      } else {
        throw new Error("Format de données inattendu pour les certificats");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        setError(
          new Error(
            "Délai d'attente dépassé : Veuillez vérifier votre connexion",
          ),
        );
      } else if (err.response) {
        setError(
          new Error(
            `Erreur du serveur : ${err.response.status} - ${
              err.response.data?.message || "Erreur inconnue"
            }`,
          ),
        );
      } else if (err.request) {
        setError(
          new Error(
            "Erreur réseau : Impossible d'atteindre le serveur. Veuillez vérifier votre connexion.",
          ),
        );
      } else {
        setError(new Error(`Erreur : ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, retryCount]);

  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  // Show loading state if first load
  if (loading && retryCount === 0) {
    return (
      <section className="certificates__area pt-4 pb-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6">
              <div className="section__title text-center mb-4">
                <span className="sub-title">Certifications</span>
                <h2 className="title">Nos Certificats</h2>
                <p>Découvrez les certificats obtenus par nos apprenants</p>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "3rem", height: "3rem" }}
              >
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3">Chargement des certificats...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state when no certificates
  if (error && (!Array.isArray(certificates) || certificates.length === 0)) {
    return (
      <section className="certificates__area pt-4 pb-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6">
              <div className="section__title text-center mb-4">
                <span className="sub-title">Certifications</span>
                <h2 className="title">Nos Certificats</h2>
                <p>Découvrez les certificats obtenus par nos apprenants</p>
              </div>
            </div>
          </div>
          <div className="alert alert-danger" role="alert">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle fa-2x mb-3 text-danger"></i>
              <h4>Erreur de chargement</h4>
              <p className="mb-3">{error.message}</p>
              <button
                className="btn btn-primary"
                onClick={handleRetry}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>{" "}
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-redo me-2"></i> Réessayer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="certificates__area pt-4 pb-4">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6">
            <div className="section__title text-center mb-4">
              <span className="sub-title">Certifications</span>
              <h2 className="title">Nos Certificats</h2>
              <p>Découvrez les certificats obtenus par nos apprenants</p>
            </div>
          </div>
        </div>

        {/* Show warning if there was an error but we have some data */}
        {error && (
          <div className="alert alert-warning mb-4" role="alert">
            <div className="d-flex justify-content-between align-items-center">
              <small>
                <i className="fas fa-exclamation-triangle me-2"></i> Données
                partielles: {error.message}
              </small>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleRetry}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>{" "}
                    Chargement...
                  </>
                ) : (
                  "Actualiser"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Loading state during retry */}
        {loading && retryCount > 0 && (
          <div className="d-flex justify-content-center align-items-center py-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <span className="ms-3">Mise à jour des certificats...</span>
          </div>
        )}

        {!loading &&
        (!Array.isArray(certificates) || certificates.length === 0) ? (
          <div className="text-center py-4">
            <div className="alert alert-info">
              <h5>Aucun certificat trouvé</h5>
              <p className="mb-0">
                {userId
                  ? "Aucun certificat n'a été trouvé pour cet utilisateur."
                  : "Aucun certificat n'est disponible pour le moment."}
              </p>
            </div>
          </div>
        ) : (
          <Swiper
            slidesPerView={1}
            spaceBetween={20}
            navigation={true}
            breakpoints={{
              640: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 4 },
            }}
            modules={[Navigation]}
            className="certificates__slider"
          >
            {certificates.map((certificate) => (
              <SwiperSlide key={certificate.id_certification}>
                <div
                  className="certificate__item"
                  data-aos="fade-up"
                  style={{
                    maxWidth: "280px",
                    margin: "0 auto",
                    height: "100%",
                  }}
                >
                  <div
                    className="certificate__thumb"
                    style={{ height: "160px", overflow: "hidden" }}
                  >
                    <img
                      src={
                        certificate.image_path ||
                        "assets/img/certificates/default-certificate.jpg"
                      }
                      alt={certificate.nom_certification || "Certificat"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.src =
                          "assets/img/certificates/default-certificate.jpg";
                      }}
                    />
                  </div>
                  <div
                    className="certificate__content"
                    style={{ padding: "15px" }}
                  >
                    <h4
                      className="title"
                      style={{
                        fontSize: "16px",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      <Link
                        to={`/certificate-detail/${certificate.id_certification}`}
                      >
                        {certificate.nom_certification || "Certificat"}
                      </Link>
                    </h4>
                    {certificate.description_certification && (
                      <p
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.4",
                          marginBottom: "12px",
                          height: "40px",
                          overflow: "hidden",
                        }}
                      >
                        {certificate.description_certification.length > 70
                          ? certificate.description_certification.substring(
                              0,
                              70,
                            ) + "..."
                          : certificate.description_certification}
                      </p>
                    )}
                    <div
                      className="certificate__meta"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        fontSize: "12px",
                      }}
                    >
                      {certificate.user_name && (
                        <div
                          className="certificate__user"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <User size={14} style={{ marginRight: "5px" }} />
                          <span>{certificate.user_name}</span>
                        </div>
                      )}
                      {certificate.date_obtention && (
                        <div
                          className="certificate__date"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Calendar size={14} style={{ marginRight: "5px" }} />
                          <span>
                            {new Date(
                              certificate.date_obtention,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {certificate.duree_validite && (
                        <div
                          className="certificate__formation"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Award size={14} style={{ marginRight: "5px" }} />
                          <span>
                            Valide: {certificate.duree_validite}{" "}
                            {certificate.duree_validite === 1 ? "an" : "ans"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </section>
  );
};

export default CertificatesDisplay;
